import { useEffect, useReducer, useRef, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ReportDataConfiguratorV2 } from "./ReportDataConfiguratorV2";
import { ReportChartConfiguratorV2 } from "./ReportChartConfiguratorV2";
import { Separator } from "@/components/ui/separator";
import { DatasetName } from "@/common/constants";
import {
  ReportDesignerState,
  ReportStateReducerActionType,
} from "@/common/reportDesignerStateReducer";
import { reportDataConfiguratorReducer } from "./reportDataConfiguratorReducer";
import { useCurrentWorkspace } from "@/store/authContext/authContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface ReportConfigSidebarV2Props {
  reportDesignerState: ReportDesignerState;
  reportDesignerDispatch: React.Dispatch<ReportStateReducerActionType>;
}

const ReportConfigSidebarV2: React.FC<ReportConfigSidebarV2Props> = ({
  reportDesignerState: state,
  reportDesignerDispatch,
}) => {
  const currentWorkspace = useCurrentWorkspace();
  const [isDatasetSwitchDialogOpen, setIsDatasetSwitchDialogOpen] =
    useState(false);
  const [pendingDataset, setPendingDataset] = useState<DatasetName | null>(
    null,
  );
  const lastConfirmedSelectedDatasets = useRef<DatasetName[]>([
    "main",
    "recruitment_applications",
  ]);

  const [configuratorState, configuratorDispatch] = useReducer(
    reportDataConfiguratorReducer,
    {
      selectedDatasets: ["main"],
      availableMeasures: [],
      availableDimensions: [],
      availableFilters: [],
      reportConfig: state.reportConfig,
      datasetConfigs: currentWorkspace.dataset_field_configs,
    },
  );

  useEffect(() => {
    configuratorDispatch({
      type: "syncWithReportDesignerState",
      payload: {
        reportConfig: state.reportConfig,
      },
    });
  }, [state.reportConfig]);

  useEffect(() => {
    if (pendingDataset) return;

    if (
      configuratorState.reportConfig.data.datasetName !==
      state.reportConfig.data.datasetName
    ) {
      return;
    }
    if (
      !configuratorState.selectedDatasets.includes(
        state.reportConfig.data.datasetName,
      )
    ) {
      const nextDataset = configuratorState.selectedDatasets[0];
      const hasSavedReport = state.reportId !== null;
      if (hasSavedReport) {
        setPendingDataset(nextDataset);
        setIsDatasetSwitchDialogOpen(true);
      } else {
        lastConfirmedSelectedDatasets.current =
          configuratorState.selectedDatasets;
        reportDesignerDispatch({ type: "setDataset", payload: nextDataset });
      }
    } else {
      lastConfirmedSelectedDatasets.current =
        configuratorState.selectedDatasets;
    }
  }, [
    configuratorState.selectedDatasets,
    configuratorState.reportConfig.data.datasetName,
    state.reportConfig.data.datasetName,
    state.reportId,
    pendingDataset,
    reportDesignerDispatch,
  ]);

  const cancelDatasetSwitch = () => {
    configuratorDispatch({
      type: "restoreSelectedDatasets",
      payload: {
        selectedDatasets: lastConfirmedSelectedDatasets.current,
      },
    });
    setPendingDataset(null);
    setIsDatasetSwitchDialogOpen(false);
  };

  const confirmDatasetSwitch = () => {
    if (!pendingDataset) return;

    lastConfirmedSelectedDatasets.current = configuratorState.selectedDatasets;
    setPendingDataset(null);
    setIsDatasetSwitchDialogOpen(false);
    reportDesignerDispatch({ type: "setDataset", payload: pendingDataset });
  };

  return (
    <>
      <div className="w-96 border-l border-gray-200 dark:border-gray-700 h-full flex flex-col">
        <Tabs defaultValue="data" className="flex flex-col h-full">
          <div className="px-4 my-7">
            <TabsList className="flex w-full">
              <TabsTrigger
                value="data"
                disabled={state.reportIsInSnapshotMode}
                className="uppercase flex-1 px-6 py-2 text-xs border border-gray-800 data-[state=active]:text-gray-500 data-[state=active]:bg-gray-700  data-[state=active]:text-white mx-1 text-gray-500 bg-white"
              >
                Data
              </TabsTrigger>
              <TabsTrigger
                value="chart"
                disabled={state.reportIsInSnapshotMode}
                className="uppercase flex-1 px-6 py-2 text-xs border border-gray-800 data-[state=active]:text-gray-500 data-[state=active]:bg-gray-700  data-[state=active]:text-white mx-1 text-gray-500 bg-white"
              >
                Design
              </TabsTrigger>
            </TabsList>
          </div>
          <Separator />
          <div className="flex-1 overflow-hidden">
            {state.reportIsInSnapshotMode ? (
              <div className="px-4 py-2 bg-blue-50 text-blue-800 text-sm">
                Viewing read-only snapshot
              </div>
            ) : (
              <ScrollArea className="h-full px-4">
                <TabsContent value="data" className="mt-0">
                  <ReportDataConfiguratorV2
                    reportDesignerState={state}
                    reportDesignerDispatch={reportDesignerDispatch}
                    configuratorState={configuratorState}
                    configuratorDispatch={configuratorDispatch}
                    currentWorkspace={currentWorkspace}
                  />
                </TabsContent>
                <TabsContent value="chart" className="mt-0">
                  <ReportChartConfiguratorV2
                    reportDesignerState={state}
                    reportDesignerDispatch={reportDesignerDispatch}
                  />
                </TabsContent>
              </ScrollArea>
            )}
          </div>
        </Tabs>
      </div>
      <Dialog
        open={isDatasetSwitchDialogOpen}
        onOpenChange={(open) => {
          if (!open) cancelDatasetSwitch();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Discard unsaved changes?</DialogTitle>
            <DialogDescription>
              Switching datasets will discard your unsaved report changes.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={cancelDatasetSwitch}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDatasetSwitch}>
              Switch dataset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReportConfigSidebarV2;
